import { useEffect, useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { ProgressSpinner } from "primereact/progressspinner";
import { MonitoreoService } from "../../service/monitoreo/MonitoreoService";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColorPicker } from "primereact/colorpicker";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";

export const PlantillaColor = ({ generic, permss, toast, catalogo, funcionalidades, tipo }) => {

    const monitoreoService = new MonitoreoService();
    const [activeIndex, setActiveIndex] = useState(null);
    const [savingIndexes, setSavingIndexes] = useState(new Set()); // Para evitar multiples ediciones
    const [load, setLoad] = useState(false);
    const [loadingTabla, setLoadingTabla] = useState(false);
    const [loadingAreas, setLoadingAreas] = useState(false);
    const [registers, setRegisters] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [listaAreas, setListaAreas] = useState([]);
    const [empresa, setEmpresa] = useState(null);
    const [areaNegocio, setAreaNegocio] = useState(null);

    const niveles = ["RIESGO_ACCIONISTA", "RIESGO_ACCIONISTA_JURIDICO", "RIESGO_EMPLEADO", "RIESGO_CLIENTE", "RIESGO_PROVEEDOR"];

    const [lazyState, setLazyState] = useState({
        first: 0,
        rows: parseInt(process.env.REACT_APP_ROWS_NUM),
        page: 1,
        sortField: null,
        sortOrder: null,
        filters: {
            nombre: { value: '', matchMode: 'contains' },
        }
    });

    const onPage = (event) => {
        setLazyState(event);
    }

    const onSort = (event) => {
        setLazyState(event);
    }

    const onFilter = (event) => {
        event['first'] = 0;
        setLazyState(event);
    }

    useEffect(() => {
        if (activeIndex === null) return;
        loadLazyData();
    }, [lazyState]);

    useEffect(() => {
        if (catalogo?.empresas?.length === 1) {
            const empresaUnica = catalogo.empresas[0];
            setEmpresa(empresaUnica);
            consultarAreasNegocio(empresaUnica);
        }
    }, []);

    const loadLazyData = () => {
        setLoadingTabla(true);
        const _lazyState = lazyState;
        delete _lazyState.rtipo;
        const tipo = niveles[activeIndex];

        const params = { lazyEvent: JSON.stringify(_lazyState), tipo: tipo, areaNegocio: areaNegocio, empresa: empresa }

        monitoreoService.consultarInfo("matriz/config-color/lista", params)
            .then((res) => {
                setRegisters(res.data.registros);
                setTotalRecords(res.total);
            })
            .catch((error) => {
                generic.funcDeleteError(error, toast);
            }).finally(() => {
                setLoadingTabla(false);
            });
    }

    const handleTabChange = (e) => {
        const idx = e.index;
        if (idx === null) {
            setActiveIndex(null);
            setRegisters(null);
            return;
        }
        setActiveIndex(idx);
        setRegisters(null);
        setLoad(true);
        setLoadingTabla(true);
        const tipo = niveles[idx];
        const _lazyState = lazyState;
        delete _lazyState.rtipo;

        const params = { lazyEvent: JSON.stringify(_lazyState), tipo: tipo, areaNegocio: areaNegocio, empresa: empresa }

        monitoreoService.consultarInfo("matriz/config-color/lista", params)
            .then((res) => {
                setRegisters(res.data.registros);
                setTotalRecords(res.total);
                if (res.message) {
                    toast.current.show({ severity: 'info', summary: 'Info', detail: res.message });
                }
            })
            .catch((error) => {
                generic.funcDeleteError(error, toast);
            }).finally(() => {
                setLoad(false);
                setLoadingTabla(false);
            });
    };

    const consultarAreasNegocio = (empresaSeleccionada) => {
        if (!empresaSeleccionada?.cat_id) return;
        setEmpresa(empresaSeleccionada);
        setLoad(true);
        setLoadingAreas(true);
        monitoreoService.consultar(empresaSeleccionada.cat_id, 'administracion/empresa-areas')
            .then(res => {
                const lista = res.data;
                setListaAreas(lista);
                if (lista.length === 1) {
                    setAreaNegocio(lista[0]);
                } else if (lista.length > 1) {
                    setAreaNegocio(null);
                } else {
                    setAreaNegocio(null);
                }
            })
            .catch(error => {
                generic.funcDeleteError(error, toast);
            })
            .finally(() => {
                setLoad(false);
                setLoadingAreas(false);
            });
    };

    const CELL_HEIGHT = 32;

    const genericCellBody = (field) => (rowData) => {
        // Descomentar lo siguiente cuando se pueda editar los rangos maximos y minimo para la matriz de cliente y proveedor y eliminar el otro isSaving
        // const index = registers.findIndex(r => r.id === rowData.id);
        // const isSaving = savingIndexes.has(index);
        const isSaving = false;

        return (
            <div style={{ height: CELL_HEIGHT, display: 'flex', alignItems: 'center' }}>
                {isSaving ? (
                    <ProgressBar mode="indeterminate" style={{ width: '50px', height: '0.5rem' }} />
                ) : (
                    <span>{rowData[field]}</span>
                )}
            </div>
        );
    };

    const colorCellBody = (field) => (rowData) => {
        const index = registers.findIndex(r => r.id === rowData.id);
        const isSaving = savingIndexes.has(index);
        const colorValue = rowData[field] ?? '';

        return (
            <div style={{ height: CELL_HEIGHT, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {isSaving ? (
                    <ProgressBar mode="indeterminate" style={{ width: '50px', height: '0.5rem' }} />
                ) : (
                    <>
                        <div style={{
                            backgroundColor: colorValue.startsWith('#') ? colorValue : `#${colorValue}`, width: '26px',
                            height: '26px', border: '1px solid #ccc', borderRadius: '3px', marginRight: '20px'
                        }} />
                        <span>{colorValue}</span>
                    </>
                )}
            </div>
        );
    };

    const EDITOR_HEIGHT = 32;

    // Descomentar lo siguiente cuando se pueda editar los rangos maximos y minimo para la matriz de cliente y proveedor
    // const numberEditor = (options) => {
    //     return (
    //         <InputNumber value={options.value} onValueChange={(e) => options.editorCallback(e.value)} useGrouping={false} minFractionDigits={0} maxFractionDigits={2} style={{ height: EDITOR_HEIGHT }} />
    //     );
    // };

    // const textEditor = (options) => {
    //     return (
    //         <InputText value={options.value} onChange={(e) => options.editorCallback(e.target.value)} style={{ height: EDITOR_HEIGHT }} />
    //     );
    // };

    const colorEditor = (options) => {
        const val = options.value || '';
        const colorValue = val.startsWith('#') ? val : `#${val}`;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', height: EDITOR_HEIGHT }}>
                <ColorPicker format="hex" value={colorValue} onChange={(e) => options.editorCallback(e.value)} style={{ width: '26px', height: '1.5rem', padding: 0, marginBottom: '7px' }} />
                <InputText value={val} placeholder="hex" style={{ width: '5rem' }}
                    onChange={(e) => {
                        let v = e.target.value.replace(/^#/, '');
                        if (/^[0-9A-Fa-f]{0,6}$/.test(v)) {
                            options.editorCallback(v);
                        }
                    }}
                />
            </div>
        );
    };

    // Funcion para el control de edición
    const addSavingIndex = (index) => {
        setSavingIndexes(prev => new Set(prev).add(index));
    };

    // Funcion para el control de edición
    const removeSavingIndex = (index) => {
        setSavingIndexes(prev => { const updated = new Set(prev); updated.delete(index); return updated; });
    };

    const handleRowEditComplete = (e) => {
        const { newData, index } = e;
        addSavingIndex(index);
        monitoreoService.editar(newData, "matriz/config-color/editar")
            .then((res) => {
                const updated = [...registers];
                updated[index] = res.data ?? newData;
                setRegisters(updated);
                toast.current.show({ severity: 'success', detail: res.message });
            })
            .catch((error) => {
                generic.funcDeleteError(error, toast);
            })
            .finally(() => {
                removeSavingIndex(index);
            });
    };

    const rowEditorBody = (rowData, { rowEditor }) => {
        const editing = rowEditor.editing;
        const index = registers.findIndex(r => r.id === rowData.id);
        const isSaving = savingIndexes.has(index);
        const isAnySaving = savingIndexes.size > 0;

        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '4rem', position: 'relative' }}>
                {isSaving ? (
                    <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
                ) : (
                    <>
                        <Button icon="pi pi-pencil" className="p-button-rounded p-button-text icono-tabla-edit" onClick={rowEditor.onInitClick}
                            {...(!editing && { tooltip: 'Editar', tooltipOptions: { position: 'bottom' } })}
                            style={{ opacity: editing ? 0 : 1, pointerEvents: editing ? 'none' : 'auto', transition: 'opacity .1s' }}
                        />
                        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '1rem', opacity: editing ? 1 : 0, pointerEvents: editing ? 'auto' : 'none', transition: 'opacity .1s' }}>
                            <Button icon="pi pi-check" className="p-button-rounded p-button-text icono-tabla-edit" onClick={rowEditor.onSaveClick} disabled={isAnySaving}
                                {...(editing && { tooltip: 'Guardar', tooltipOptions: { position: 'bottom' } })}
                            />
                            <Button icon="pi pi-times" className="p-button-rounded p-button-text icono-tabla-edit" onClick={rowEditor.onCancelClick}
                                {...(editing && { tooltip: 'Cancelar', tooltipOptions: { position: 'bottom' } })}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderTabla = (tipoActivo) => {
        return (
            <DataTable value={registers} lazy paginator scrollable dataKey="id" filterDisplay="row" loading={loadingTabla} onRowEditComplete={handleRowEditComplete}
                first={lazyState.first} rows={lazyState.rows} totalRecords={totalRecords} onPage={onPage} onSort={onSort} editMode="row"
                onFilter={onFilter} sortField={lazyState.sortField} sortOrder={lazyState.sortOrder} filters={lazyState.filters} >
                <Column field="nombre" header="Nombre" sortable filter headerStyle={{ width: '200px' }} bodyStyle={{ width: '100px' }} />
                {(tipoActivo === "RIESGO_CLIENTE" || tipoActivo === "RIESGO_PROVEEDOR") && (
                    // <Column field="minimo" header="Mínimo" body={genericCellBody('minimo')} editor={numberEditor} bodyStyle={{ width: '100px' }} />
                    <Column field="minimo" header="Mínimo" body={genericCellBody('minimo')} bodyStyle={{ width: '100px' }} />
                )}
                {(tipoActivo === "RIESGO_CLIENTE" || tipoActivo === "RIESGO_PROVEEDOR") && (
                    // <Column field="maximo" header="Máximo" body={genericCellBody('maximo')} editor={numberEditor} bodyStyle={{ width: '100px' }} />
                    <Column field="maximo" header="Máximo" body={genericCellBody('maximo')} bodyStyle={{ width: '100px' }} />
                )}
                {(tipoActivo === "RIESGO_CLIENTE" || tipoActivo === "RIESGO_PROVEEDOR") && (
                    // <Column field="rango" header="Rango" body={genericCellBody('rango')} editor={textEditor} bodyStyle={{ width: '100px' }} />
                    <Column field="rango" header="Rango" body={genericCellBody('rango')} bodyStyle={{ width: '100px' }} />
                )}
                <Column field="color" header="Color de Fondo" body={colorCellBody('color')} editor={colorEditor} />
                <Column field="color_texto" header="Color del Texto" body={colorCellBody('color_texto')} editor={colorEditor} />
                <Column rowEditor body={rowEditorBody} headerStyle={{ width: '10rem' }} bodyStyle={{ textAlign: 'center' }} />
            </DataTable>
        );
    }

    const renderLoader = () => (
        <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
            <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="5" />
        </div>
    );

    const siOpcion = (strOpcion) => {
        return permss?.actualizar && (funcionalidades === 'PRELAFIT' || funcionalidades?.includes(strOpcion));
    }

    return (
        <>
            <div className="flex justify-content-center">
                <div
                    className={`flex ${catalogo?.empresas?.length > 1 ? 'gap-5' : ''}`}
                    style={{ flexWrap: 'wrap', justifyContent: 'center' }}
                >
                    {((catalogo?.empresas?.length > 1)) && (
                        <div className="field" style={{ width: '250px' }}>
                            <div style={{ marginBottom: '5px' }}>
                                <b>EMPRESA</b>
                            </div>
                            <Dropdown options={catalogo?.empresas} value={empresa} optionLabel="nombre" placeholder="Seleccione una empresa" className="w-full"
                                onChange={(e) => { setEmpresa(e.value); setAreaNegocio(null); setActiveIndex(null); setRegisters(null); consultarAreasNegocio(e.value); }}
                            />
                        </div>
                    )}

                    {(tipo === "todos" || tipo === "RIESGO_CLIENTE") && (
                        <>
                            {!loadingAreas && listaAreas?.length > 0 && (
                                <div className="field" style={{ width: '250px' }}>
                                    <div style={{ marginBottom: '5px' }}>
                                        <b>ÁREAS DE NEGOCIO</b>
                                    </div>
                                    <Dropdown options={listaAreas} value={areaNegocio} optionLabel="nombre" placeholder="Seleccione un área" className="w-full"
                                        onChange={(e) => { setAreaNegocio(e.value); setRegisters(null); setActiveIndex(null) }}
                                    />
                                </div>
                            )}

                            {loadingAreas && (
                                <div className="flex align-items-center justify-content-center" style={{ width: '220px', height: '60px' }}>
                                    <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="6" />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <Accordion activeIndex={activeIndex} onTabChange={handleTabChange}>
                {(tipo === "todos" || tipo === "RIESGO_ACCIONISTA") && (
                    <AccordionTab header="RIESGO ACCIONISTA">
                        {activeIndex === 0 && (load ? renderLoader() : renderTabla("RIESGO_ACCIONISTA"))}
                    </AccordionTab>
                )}
                {(tipo === "todos" || tipo === "RIESGO_ACCIONISTA_JURIDICO") && (
                    <AccordionTab header="RIESGO ACCIONISTA JURÍDICO">
                        {activeIndex === 1 && (load ? renderLoader() : renderTabla("RIESGO_ACCIONISTA_JURIDICO"))}
                    </AccordionTab>
                )}
                {(tipo === "todos" || tipo === "RIESGO_EMPLEADO") && (
                    <AccordionTab header="RIESGO EMPLEADO">
                        {activeIndex === 2 && (load ? renderLoader() : renderTabla("RIESGO_EMPLEADO"))}
                    </AccordionTab>
                )}
                {(tipo === "todos" || tipo === "RIESGO_CLIENTE") && (
                    <AccordionTab header="RIESGO CLIENTE">
                        {activeIndex === 3 && (load ? renderLoader() : renderTabla("RIESGO_CLIENTE"))}
                    </AccordionTab>
                )}
                {(tipo === "todos" || tipo === "RIESGO_PROVEEDOR") && (
                    <AccordionTab header="RIESGO PROVEEDOR">
                        {activeIndex === 4 && (load ? renderLoader() : renderTabla("RIESGO_PROVEEDOR"))}
                    </AccordionTab>
                )}
            </Accordion>
        </>
    );
}
